import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, message, Spin, Tag} from 'antd';
import {ArrowLeftOutlined, DownOutlined, PauseOutlined, ReloadOutlined, UpOutlined} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import {
    BacktestAiReportResponse,
    BacktestResultResponse,
    backtestService,
    BacktestTaskResponse,
    HoldingProfitData
} from '../../services/backtestService';
import './BacktestDetailPage.css';

interface KLineData {
    date: string;
    profit: number;
}

const BacktestDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [task, setTask] = useState<BacktestTaskResponse | null>(null);
    const [result, setResult] = useState<BacktestResultResponse | null>(null);
    const [chartData, setChartData] = useState<KLineData[]>([]);
    const [btcHoldingData, setBtcHoldingData] = useState<HoldingProfitData[]>([]);
    const [ethHoldingData, setEthHoldingData] = useState<HoldingProfitData[]>([]);
    const [logCollapsed, setLogCollapsed] = useState(false);
    const [aiReport, setAiReport] = useState<BacktestAiReportResponse | null>(null);
    const [aiReportLoading, setAiReportLoading] = useState(false);

    // 根据任务状态控制日志折叠：运行时展开，结束后折叠
    useEffect(() => {
        if (task?.status === 'RUNNING' || task?.status === 'PENDING') {
            setLogCollapsed(false);
        } else {
            setLogCollapsed(true);
        }
    }, [task?.status]);

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    // 实时刷新日志（RUNNING 状态下每5秒刷新）
    useEffect(() => {
        if (!id || task?.status !== 'RUNNING') return;

        const interval = setInterval(() => {
            backtestService.getBacktestResult(Number(id))
                .then(data => {
                    setResult(prev => prev ? {...prev, logs: data.logs} : data);
                })
                .catch(() => {
                });
        }, 5000);

        return () => clearInterval(interval);
    }, [id, task?.status]);

    const loadData = async () => {
        if (!id) return;
        setLoading(true);
        try {
            // 获取任务信息
            const tasksRes = await backtestService.getBacktests(undefined, 0, 100);
            const foundTask = (tasksRes?.content || []).find(t => t.id === Number(id));
            if (foundTask) {
                setTask(foundTask);
            }

            // 获取回测结果
            const resultData = await backtestService.getBacktestResult(Number(id));
            setResult(resultData);

            // 解析资金曲线数据
            if (resultData.dailyProfit && resultData.dailyProfit.length > 0) {
                const parsed: KLineData[] = [];
                let cumProfit = 0;
                for (const item of resultData.dailyProfit) {
                    const date = item.date || item[0] || '';
                    const profit = parseFloat(item.profit || item[1] || 0);
                    cumProfit += profit;
                    parsed.push({date, profit: cumProfit});
                }
                setChartData(parsed);
            }

            // 解析BTC持仓收益数据
            if (resultData.btcHoldingProfit && resultData.btcHoldingProfit.length > 0) {
                setBtcHoldingData(resultData.btcHoldingProfit);
            }

            // 解析ETH持仓收益数据
            if (resultData.ethHoldingProfit && resultData.ethHoldingProfit.length > 0) {
                setEthHoldingData(resultData.ethHoldingProfit);
            }

            // 获取AI解读报告（仅当任务成功时）
            setAiReportLoading(true);
            backtestService.getBacktestAiReport(Number(id))
                .then(report => {
                    setAiReport(report);
                })
                .catch(() => {
                    setAiReport(null);
                })
                .finally(() => {
                    setAiReportLoading(false);
                });
        } catch (e) {
            console.error('Failed to load backtest data', e);
            message.error('加载回测数据失败');
        } finally {
            setLoading(false);
        }
    };

    const handlePause = async () => {
        if (!id) return;
        try {
            await backtestService.pauseBacktest(Number(id));
            message.success('已暂停');
            loadData();
        } catch (e) {
            message.error('暂停失败');
        }
    };

    const formatNumber = (val: number, decimals = 2): string => {
        if (val === null || val === undefined) return '-';
        return val.toLocaleString('en-US', {minimumFractionDigits: decimals, maximumFractionDigits: decimals});
    };

    // 格式化时间（不做时区转换，直接截取原始字符串 + 星期几）
    const formatDate = (dateStr: string | undefined, weekday?: string): string => {
        if (!dateStr) return '-';
        // 直接截取 YYYY-MM-DD HH:mm 部分
        const base = dateStr.substring(0, 16);
        // 星期映射
        const weekdayMap: Record<string, string> = {
            '0': '周日', '1': '周一', '2': '周二', '3': '周三',
            '4': '周四', '5': '周五', '6': '周六'
        };
        const wd = weekday ? (weekdayMap[weekday] || '') : '';
        return wd ? `${base} (${wd})` : base;
    };

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'RUNNING':
                return <Tag color="blue">运行中</Tag>;
            case 'PENDING':
                return <Tag color="default">等待中</Tag>;
            case 'PAUSED':
                return <Tag color="orange">已暂停</Tag>;
            case 'SUCCESS':
                return <Tag color="green">成功</Tag>;
            case 'FAILED':
                return <Tag color="red">失败</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const calcRunDays = (): string => {
        if (!task?.createdAt) return '-';
        const start = new Date(task.createdAt);
        const end = task.finishedAt ? new Date(task.finishedAt) : new Date();
        const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return `${diff} 天`;
    };

    const renderProfitChart = () => {
        const width = 500;
        const height = 160;
        const padding = {top: 20, right: 20, bottom: 30, left: 50};
        const chartWidth = width - padding.left - padding.right;
        const chartHeight = height - padding.top - padding.bottom;

        // 当策略无数据时，生成示例数据
        const isDemoMode = chartData.length === 0;
        const demoPoints = 20;

        // 策略数据：真实数据或示例数据（收益=0水平线）
        const displayChartData = isDemoMode
            ? Array.from({length: demoPoints}, (_, i) => ({
                date: `Day ${i + 1}`,
                profit: 0
            }))
            : chartData;

        // BTC数据：真实数据或从首尾插值
        let displayBtcProfits: number[] = [];
        if (isDemoMode && btcHoldingData.length > 0) {
            // 从真实BTC数据的首尾插值
            const startPct = btcHoldingData[0].cumulativeProfitPct;
            const endPct = btcHoldingData[btcHoldingData.length - 1].cumulativeProfitPct;
            displayBtcProfits = Array.from({length: demoPoints}, (_, i) =>
                startPct + (endPct - startPct) * (i / (demoPoints - 1))
            );
        } else if (btcHoldingData.length > 0) {
            displayBtcProfits = btcHoldingData.map(d => d.cumulativeProfitPct);
        }

        // ETH数据
        const ethProfits = ethHoldingData.map(d => d.cumulativeProfitPct);

        // 计算Y轴范围
        const profits = displayChartData.map(d => d.profit);
        const allProfits = [...profits, ...displayBtcProfits, ...ethProfits];
        const minProfit = Math.min(0, ...allProfits);
        const maxProfit = Math.max(0, ...allProfits);
        const profitRange = maxProfit - minProfit || 1;

        const xScale = (i: number, dataLen: number) => padding.left + (i / (dataLen - 1 || 1) * chartWidth);
        const yScale = (v: number) => padding.top + chartHeight - ((v - minProfit) / profitRange * chartHeight);

        // 策略曲线
        const strategyPoints = displayChartData.map((d, i) => `${xScale(i, displayChartData.length)},${yScale(d.profit)}`).join(' ');
        const zeroY = yScale(0);

        // BTC曲线
        const btcPoints = displayBtcProfits.length > 0
            ? displayBtcProfits.map((p, i) => `${xScale(i, displayBtcProfits.length)},${yScale(p)}`).join(' ')
            : '';

        // Y轴标签
        const yLabels = [maxProfit, 0, minProfit];

        // X轴标签
        const xLabels: { idx: number; label: string }[] = [];
        const showCount = 3;
        for (let i = 0; i < showCount; i++) {
            const idx = Math.floor(i / (showCount - 1) * (displayChartData.length - 1));
            const d = displayChartData[idx];
            if (d) {
                xLabels.push({idx, label: d.date.length > 8 ? d.date.substring(2, 10) : d.date});
            }
        }

        // 如果完全没有数据（策略和BTC都无），显示暂无数据
        if (displayChartData.length === 0) {
            return <div className="chart-empty">暂无数据</div>;
        }

        return (
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`}>
                {/* 示例数据提示 */}
                {isDemoMode && (
                    <text x={width - padding.right - 50} y={padding.top} fill="var(--pt-text-dim)" fontSize="10"
                          textAnchor="end">
                        示例数据
                    </text>
                )}
                <line x1={padding.left} y1={zeroY} x2={padding.left + chartWidth} y2={zeroY}
                      stroke="var(--pt-border-subtle)"
                      strokeWidth="1"/>
                {/* 策略曲线填充区域 */}
                <polygon points={`${padding.left},${zeroY} ${strategyPoints} ${padding.left + chartWidth},${zeroY}`}
                         fill="var(--pt-primary-dim)"/>
                {/* 策略曲线 */}
                <polyline points={strategyPoints} fill="none" stroke="var(--pt-primary)" strokeWidth="2"/>
                {/* BTC持有曲线 */}
                {btcPoints && (
                    <polyline points={btcPoints} fill="none" stroke="var(--pt-bear)" strokeWidth="1.5"
                              strokeDasharray="4,2"/>
                )}
                {yLabels.map((v, i) => (
                    <text key={i} x={padding.left - 5} y={yScale(v) + 4} fill="var(--pt-text-dim)" fontSize="9"
                          textAnchor="end">
                        {v.toFixed(1)}%
                    </text>
                ))}
                {xLabels.map((l, i) => (
                    <text key={i} x={xScale(l.idx, displayChartData.length)} y={height - 8} fill="var(--pt-text-dim)"
                          fontSize="9" textAnchor="middle">
                        {l.label}
                    </text>
                ))}
            </svg>
        );
    };

    if (loading) {
        return <div className="detail-loading"><Spin size="large"/></div>;
    }

    const isRunning = task?.status === 'RUNNING' || task?.status === 'PENDING' || task?.status === 'PAUSED';
    // 根据任务状态决定返回哪个tab：成功/失败返回历史tab，运行中/暂停/等待返回运行tab
    const backTab = (task?.status === 'SUCCESS' || task?.status === 'FAILED') ? 'history' : 'running';
    // 累计盈亏绝对值（USDT）
    const profitAbs = result?.totalProfitAbs || 0;
    // 累计收益率（百分比）
    const cumulativeReturn = result?.totalProfitPct || 0;
    // 通过 累计盈亏 / 累计收益率 反推初始资金，避免硬编码
    // 公式：initialCapital = profitAbs / (cumulativeReturn / 100)
    // 当收益率为0时使用默认值10000，避免除零错误
    const initialCapital = 0 !== cumulativeReturn ? profitAbs / (cumulativeReturn / 100) : 10000;
    // 当前净值 = 初始资金 + 累计盈亏
    const currentNav = initialCapital + profitAbs;

    // 持仓数据（后端计算）
    const currentPositionAmount = result?.currentPositionAmount || 0;
    const currentPositionValue = result?.currentPositionValue || 0;
    const currentPosition = currentPositionAmount > 0 ? `${Number(currentPositionAmount).toFixed(6)} BTC` : '0 BTC';
    const positionValue = currentPositionAmount > 0 ? `≈ ${Number(currentPositionValue).toFixed(0)} USDT` : '≈ 0 USDT';

    // 盈亏显示：根据正负值动态调整符号和颜色
    const isProfit = profitAbs >= 0;
    const profitSign = isProfit ? '+' : '';
    const profitClass = isProfit ? 'up' : 'down';
    const profitArrow = isProfit ? '▲' : '▼';

    const signalsCount = result?.totalTrades || 0;
    const maxDrawdown = result?.maxDrawdownPct || 0;

    return (
        <div className="detail-page">
            <div className="detail-header">
                <div className="header-left">
                    <Button type="text" icon={<ArrowLeftOutlined/>}
                            onClick={() => navigate(`/strategy/backtest?tab=${backTab}`)} className="back-btn"/>
                    <div>
                        <h2>{task?.taskName || `模拟盘 · ${task?.strategyName || '回测任务'}`}</h2>
                        <p>
                            {getStatusTag(task?.status || '')}
                            {isRunning ? ` · 运行中 · 已运行 ${calcRunDays()}` : ''}
                            {task?.status === 'SUCCESS' ? ` · 已完成` : ''}
                            {task?.status === 'FAILED' ? ` · 失败` : ''}
                            {task?.status === 'PAUSED' ? ` · 已暂停` : ''}
                        </p>
                    </div>
                </div>
                <div className="header-actions">
                    {task?.status === 'RUNNING' && (
                        <Button icon={<PauseOutlined/>} className="btn-pause" onClick={handlePause}>
                            暂停策略
                        </Button>
                    )}
                    <Button icon={<ReloadOutlined/>} onClick={loadData}></Button>
                </div>
            </div>

            {/* 回测日志卡片 */}
            <div className="log-card">
                <div className="log-card-header" onClick={() => setLogCollapsed(!logCollapsed)}>
                    <div className="log-card-title">
                        <span className="log-icon">📋</span>
                        回测运行日志
                        {task?.status === 'RUNNING' && (
                            <Tag color="blue" style={{marginLeft: 8, fontSize: '0.7rem'}}>实时更新中</Tag>
                        )}
                    </div>
                    <Button
                        type="text"
                        icon={logCollapsed ? <DownOutlined/> : <UpOutlined/>}
                        className="log-collapse-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setLogCollapsed(!logCollapsed);
                        }}
                    />
                </div>
                {!logCollapsed && (
                    <div className="log-card-content">
                        <pre className="log-text">
                            {result?.logs || '暂无日志'}
                        </pre>
                    </div>
                )}
            </div>

            <div className="detail-content">
                <div className="kpi-row">
                    <div className="kpi-card">
                        <div className="kpi-label">当前净值</div>
                        <div className="kpi-val mono">{formatNumber(currentNav, 0)}</div>
                        <div className="kpi-change">
                            <span
                                className={profitClass}>{profitArrow} {profitSign}{formatNumber(profitAbs)} USDT</span>
                        </div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-label">累计收益率</div>
                        <div className={`kpi-val ${cumulativeReturn >= 0 ? 'up' : 'down'}`}>
                            {cumulativeReturn >= 0 ? '+' : ''}{cumulativeReturn.toFixed(2)}%
                        </div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-label">当前持仓</div>
                        <div className="kpi-val mono" style={{fontSize: '1.1rem'}}>{currentPosition}</div>
                        <div className="kpi-change">
                            <span style={{color: 'var(--pt-text-dim)'}}>{positionValue}</span>
                        </div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-label">已执行信号</div>
                        <div className="kpi-val mono">{signalsCount} 次</div>
                        <div className="kpi-change">
                            <span className="up">1买</span>
                            <span style={{color: 'var(--pt-text-dim)'}}> + </span>
                            <span className="down">1卖</span>
                        </div>
                    </div>
                    <div className="kpi-card">
                        <div className="kpi-label">最大回撤</div>
                        <div className="kpi-val down">{maxDrawdown.toFixed(1)}%</div>
                        <div className="kpi-change">
                            <span style={{color: 'var(--pt-text-dim)'}}>限制: -12%</span>
                        </div>
                    </div>
                </div>

                <div className="main-area">
                    <div className="panel">
                        <div className="panel-header">
                            <div className="panel-title">净值曲线 vs BTC 持有</div>
                            <div className="chart-legend">
                                <span className="legend-item">
                                    <span className="legend-line gold"></span>
                                    模拟策略
                                </span>
                                <span className="legend-item">
                                    <span className="legend-line coral"></span>
                                    BTC持有
                                </span>
                            </div>
                        </div>
                        <div className="chart-svg-wrap">
                            {renderProfitChart()}
                        </div>
                    </div>

                    <div className="panel">
                        <div className="panel-header">
                            <div className="panel-title">信号执行记录 <span
                                className="signal-count">({(result?.trades || []).length})</span></div>
                        </div>
                        <div className="signal-list">
                            {(result?.trades || []).map((trade: any, idx: number) => (
                                <div key={idx} className={`signal-item ${trade.profit_abs >= 0 ? 'buy' : 'sell'}`}>
                                    <div className="signal-header">
                                        <span className="signal-type">
                                            {trade.profit_abs >= 0 ? '▲ 买入执行' : '▼ 卖出执行'}
                                        </span>
                                        <span
                                            className="signal-time">{formatDate(trade.close_date, trade.weekday)}</span>
                                    </div>
                                    <div className="signal-detail">
                                        {trade.pair || 'BTC/USDT'} @ <span
                                        className="mono">{formatNumber(trade.close_rate, 2)}</span>
                                        {trade.leverage && trade.leverage > 1 && (
                                            <span className="mono"
                                                  style={{
                                                      marginLeft: 8,
                                                      color: 'var(--pt-primary)'
                                                  }}>{trade.leverage}x</span>
                                        )}
                                    </div>
                                    {trade.exit_reason && (
                                        <div className="signal-profit" style={{color: 'var(--pt-text-dim)'}}>
                                            平仓原因: {trade.exit_reason}
                                        </div>
                                    )}
                                    {trade.profit_abs !== undefined && (
                                        <div className="signal-profit">
                                            {trade.profit_abs >= 0 ? '盈亏：+' : '盈亏：'}{formatNumber(trade.profit_abs)} USDT
                                            ({((trade.profit_ratio || 0) * 100).toFixed(2)}%)
                                        </div>
                                    )}
                                </div>
                            ))}
                            {(!result?.trades || result.trades.length === 0) && (
                                <>
                                    {Array.from({length: 5}).map((_, idx) => (
                                        <div key={idx} className="signal-item"></div>
                                    ))}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bottom-area">
                    <div className="panel ai-report">
                        <div className="panel-header">
                            <div className="panel-title gold">✦ AI 回测分析报告</div>
                            <div className="report-time">
                                {aiReport?.createdAt
                                    ? `自动生成 · ${aiReport.createdAt.substring(11, 16)}`
                                    : aiReportLoading ? '生成中...' : '等待生成'}
                            </div>
                        </div>
                        {aiReportLoading ? (
                            <div className="report-content" style={{textAlign: 'center', padding: '2rem'}}>
                                <Spin size="small"/> AI正在分析回测数据...
                            </div>
                        ) : aiReport ? (
                            <>
                                <div className="report-content">
                                    {aiReport.reportContent}
                                </div>
                                {aiReport.statsJson && (() => {
                                    try {
                                        const stats = JSON.parse(aiReport.statsJson);
                                        return (
                                            <div className="report-stats">
                                                <div className="stat-item">
                                                    <div className="stat-label">回测 vs 实盘偏差</div>
                                                    <div className="stat-value teal">{stats.deviation || '0.5%'}</div>
                                                </div>
                                                <div className="stat-divider"></div>
                                                <div className="stat-item">
                                                    <div className="stat-label">信号执行成功率</div>
                                                    <div className="stat-value teal">{stats.successRate || '95%'}</div>
                                                </div>
                                                <div className="stat-divider"></div>
                                                <div className="stat-item">
                                                    <div className="stat-label">风险状态</div>
                                                    <div className="stat-value teal">{stats.riskLevel || '安全'}</div>
                                                </div>
                                            </div>
                                        );
                                    } catch {
                                        return null;
                                    }
                                })()}
                            </>
                        ) : (
                            <div className="report-content" style={{color: 'var(--pt-text-dim)'}}>
                                回测完成后将自动生成AI分析报告...
                            </div>
                        )}
                    </div>

                    <div className="panel backtest-report">
                        <div className="panel-header">
                            <div className="panel-title">详细回测报告</div>
                        </div>
                        {result?.backtestReportTable ? (
                            <div className="report-markdown">
                                <ReactMarkdown>{result.backtestReportTable}</ReactMarkdown>
                            </div>
                        ) : (
                            <div className="report-empty">
                                回测完成后将生成详细报告...
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BacktestDetailPage;
